import { Link } from "react-router-dom";
import { ShieldCheck, Flag, FileText, UserX, ArrowRight } from "lucide-react";
import { useVerifications } from "@/admin/hooks/useVerifications";
import { useReports } from "@/admin/hooks/useReports";
import { useAdsModeration } from "@/admin/hooks/useAdsModeration";
import { useAdminUsers } from "@/admin/hooks/useAdminUsers";
import { AdminStatusBadge } from "@/admin/components/AdminStatusBadge";

function KpiCard({
  label,
  value,
  icon: Icon,
  to,
  color,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  to: string;
  color: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className={`flex h-12 w-12 items-center justify-center rounded-full ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </Link>
  );
}

export default function AdminDashboard() {
  const { query: verifQuery } = useVerifications("pending");
  const { query: reportsQuery } = useReports("open");
  const { query: adsQuery } = useAdsModeration("underReview");
  const { query: usersQuery } = useAdminUsers();

  const pendingVerifs = verifQuery.data ?? [];
  const openReports = reportsQuery.data ?? [];
  const adsUnderReview = adsQuery.data ?? [];
  const suspendedUsers = (usersQuery.data ?? []).filter((u) => u.status === "suspended");

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Admin</h1>
        <p className="text-sm text-gray-500 mt-1">Vue d'ensemble de la modération Hirafi</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Vérifications en attente"
          value={pendingVerifs.length}
          icon={ShieldCheck}
          to="/admin/verifications"
          color="bg-blue-500"
        />
        <KpiCard
          label="Signalements ouverts"
          value={openReports.length}
          icon={Flag}
          to="/admin/signalements"
          color="bg-red-500"
        />
        <KpiCard
          label="Annonces en révision"
          value={adsUnderReview.length}
          icon={FileText}
          to="/admin/annonces"
          color="bg-yellow-500"
        />
        <KpiCard
          label="Utilisateurs suspendus"
          value={suspendedUsers.length}
          icon={UserX}
          to="/admin/users"
          color="bg-gray-500"
        />
      </div>

      {/* Listes rapides */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Vérifications en attente */}
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Vérifications récentes</h2>
            <Link to="/admin/verifications" className="flex items-center gap-1 text-xs text-primary hover:underline">
              Voir tout <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {pendingVerifs.length === 0 ? (
            <p className="text-sm text-gray-400">Aucune vérification en attente</p>
          ) : (
            <ul className="space-y-3">
              {pendingVerifs.slice(0, 5).map((v) => (
                <li key={v.artisanProfileId} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{v.fullName}</p>
                    <p className="text-xs text-gray-500">{v.specialty} · {v.wilaya}</p>
                  </div>
                  <AdminStatusBadge status={v.verificationStatus} />
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Signalements récents */}
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Signalements récents</h2>
            <Link to="/admin/signalements" className="flex items-center gap-1 text-xs text-primary hover:underline">
              Voir tout <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {openReports.length === 0 ? (
            <p className="text-sm text-gray-400">Aucun signalement ouvert</p>
          ) : (
            <ul className="space-y-3">
              {openReports.slice(0, 5).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium capitalize truncate">{r.reportType}</p>
                    <p className="text-xs text-gray-500 truncate">{r.reason}</p>
                  </div>
                  <AdminStatusBadge status={r.status} />
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Annonces signalées */}
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">Annonces en révision</h2>
            <Link to="/admin/annonces" className="flex items-center gap-1 text-xs text-primary hover:underline">
              Voir tout <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {adsUnderReview.length === 0 ? (
            <p className="text-sm text-gray-400">Aucune annonce en révision</p>
          ) : (
            <ul className="space-y-3">
              {adsUnderReview.slice(0, 5).map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{a.title}</p>
                    <p className="text-xs text-gray-500">{a.authorName} · {a.wilaya}</p>
                  </div>
                  <AdminStatusBadge status={a.moderationStatus} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
